import { useState } from "react";
import { useSite } from "@/context/SiteContext";
import { SiteFooter, SiteNav } from "@/components/media/SiteNav";
import { Eyebrow, Section, TribalRule } from "@/components/media/Sections";
import { BookingForm } from "@/components/media/Catering";
import { FlavorBar } from "@/components/media/FlavorWall";
import { DrinkMenu } from "@/components/events/DrinkMenu";
import { ContactBlock } from "@/components/events/ContactBlock";
import { EventGallery } from "@/components/events/EventGallery";
import { InstagramWall } from "@/components/events/InstagramWall";
import { CateringFaq, CateringPackages } from "@/components/events/CateringPackages";
import { InstantQuote } from "@/components/events/InstantQuote";
import { AmbientVideo } from "@/components/media/VideoEmbed";
import { useShareMeta } from "@/components/media/Share";
import { CalendarDays, Check } from "lucide-react";

/** Customer-facing home: drinks, events and booking. No academy content lives here. */
export default function Events() {
  const site = useSite();
  const catering = site?.catering;
  const [prefill, setPrefill] = useState(null);
  useShareMeta({
    title: catering?.title || "Book Smash Lemonade for your event",
    description: catering?.intro || "",
    image: "/brand/cover.jpg",
  });

  if (!site)
    return (
      <div>
        <SiteNav />
        <div className="mono px-5 py-32 text-center text-sm text-muted-foreground">Loading…</div>
      </div>
    );

  return (
    <div className="min-h-screen bg-background" data-testid="events-page">
      <SiteNav />

      <div className="relative overflow-hidden border-b border-border" data-testid="events-hero">
        <AmbientVideo
          url="/brand/event-atmosphere.mp4"
          poster="/brand/proof-crew-tent.jpg"
          className="absolute inset-0 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/85 to-[#0A0A0A]" />
        <div className="relative mx-auto max-w-6xl px-5 py-20 md:px-10 md:py-32">
          <Eyebrow>{catering.eyebrow}</Eyebrow>
          <h1 className="neon mt-3 max-w-4xl text-4xl leading-[0.95] sm:text-5xl lg:text-6xl">
            {catering.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">
            {catering.intro}
          </p>
          <ul className="mt-8 space-y-3">
            {(catering.bullets || []).map((b) => (
              <li key={b} className="flex items-start gap-3 text-base text-white">
                <Check size={15} className="mt-1 shrink-0 text-primary" />
                {b}
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href="#book"
              data-testid="events-hero-cta"
              className="display inline-flex items-center gap-2 rounded-sm bg-primary px-7 py-4 text-xl uppercase text-primary-foreground transition-colors hover:bg-[#E6C800]"
            >
              <CalendarDays size={18} />
              {catering.teaser_cta}
            </a>
            <a
              href="#menu"
              data-testid="events-hero-menu"
              className="display inline-flex items-center gap-2 rounded-sm border border-primary px-7 py-4 text-xl uppercase text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              See the menu
            </a>
          </div>
        </div>
      </div>
      <FlavorBar testId="events-flavor-bar" />

      <Section testId="events-packages" id="setups" texture="graffiti">
        <CateringPackages packages={site.cateringPackages} />
      </Section>

      <TribalRule testId="events-tribal-1" />

      <Section testId="events-menu" id="menu" tint>
        <DrinkMenu menu={site.drinkMenu} />
      </Section>

      <Section testId="events-gallery" id="gallery" texture="slime">
        <EventGallery gallery={site.eventGallery} />
      </Section>

      <Section testId="events-instagram" id="instagram" tint texture="graffiti">
        <InstagramWall wall={site.instagramWall} />
      </Section>

      <Section testId="events-booking" id="book" className="max-w-3xl" tint>
        <Eyebrow>REQUEST A DATE</Eyebrow>
        <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl">{catering.teaser_title}</h2>
        <p className="mt-4 text-base text-muted-foreground md:text-lg">{catering.teaser_body}</p>
        <div className="mt-10">
          <InstantQuote
            packages={site.cateringPackages}
            onApply={(values) => {
              setPrefill({ ...values, _at: Date.now() });
              document.querySelector('[data-testid="booking-name"]')?.focus();
            }}
          />
        </div>
        <div className="mt-10">
          <BookingForm
            catering={catering}
            services={site.cateringPackages?.services || []}
            menu={site.drinkMenu}
            prefill={prefill}
          />
        </div>
        <ContactBlock catering={catering} />
      </Section>

      <TribalRule testId="events-tribal-2" />

      <Section testId="events-faq" id="faq" texture="tribal">
        <CateringFaq faq={site.cateringFaq} />
      </Section>

      <SiteFooter contactEmail={catering.contact_email} />
    </div>
  );
}
